import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Avatar,
  Chip,
  useTheme
} from '@mui/material';
import {
  People as ContactsIcon,
  Work as DealsIcon,
  CalendarToday as CalendarIcon,
  Email as EmailIcon,
  Assessment as ReportsIcon,
  Settings as SettingsIcon,
  Receipt as InvoicesIcon,
  Help as SupportIcon,
  Dashboard as DashboardIcon,
  Task as TasksIcon
} from '@mui/icons-material';

interface NavItem {
  title: string;
  icon: React.ReactNode;
  path: string;
  color: string;
  category?: string;
  new?: boolean;
}

const QuickLinks = () => {
  const theme = useTheme();

  const navItems: NavItem[] = [
    {
      title: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      color: theme.palette.primary.main
    },
    {
      title: 'Contacts',
      icon: <ContactsIcon />,
      path: '/contacts',
      color: '#4CAF50',
      category: 'Sales'
    },
    {
      title: 'Deals',
      icon: <DealsIcon />,
      path: '/deals',
      color: '#2196F3',
      category: 'Sales',
      new: true
    },
    {
      title: 'Tasks',
      icon: <TasksIcon />,
      path: '/tasks',
      color: '#FF9800',
      category: 'Productivity'
    },
    {
      title: 'Calendar',
      icon: <CalendarIcon />,
      path: '/calendar',
      color: '#F44336',
      category: 'Productivity'
    },
    {
      title: 'Email',
      icon: <EmailIcon />,
      path: '/email',
      color: '#9C27B0',
      category: 'Communication'
    },
    {
      title: 'Reports',
      icon: <ReportsIcon />,
      path: '/reports',
      color: '#607D8B',
      category: 'Analytics'
    },
    {
      title: 'Invoices',
      icon: <InvoicesIcon />,
      path: '/invoices',
      color: '#795548',
      category: 'Finance'
    },
    {
      title: 'Settings',
      icon: <SettingsIcon />,
      path: '/settings',
      color: '#9E9E9E',
      category: 'Admin'
    },
    {
      title: 'Support',
      icon: <SupportIcon />,
      path: '/support',
      color: '#00BCD4',
      category: 'Admin'
    }
  ];

  const categories = [...new Set(navItems.map(item => item.category))].filter(Boolean);

  const handleNavigation = (path: string) => {
    // Implement your navigation logic here
    console.log(`Navigating to ${path}`);
    // window.location.href = path; // or use react-router's navigate
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        Quick Access
      </Typography>

      {/* All Links Grid */}
      <Grid container spacing={3}>
        {navItems.map((item) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={item.title}>
            <Card 
              sx={{ 
                height: '100%',
                borderLeft: `4px solid ${item.color}`,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[4]
                }
              }}
            >
              <CardActionArea 
                sx={{ height: '100%' }} 
                onClick={() => handleNavigation(item.path)}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                  <Avatar sx={{ 
                    bgcolor: `${item.color}20`, 
                    color: item.color,
                    mr: 2
                  }}>
                    {item.icon}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" component="div">
                      {item.title}
                    </Typography>
                    {item.category && (
                      <Typography variant="caption" color="text.secondary">
                        {item.category}
                      </Typography>
                    )}
                  </Box>
                  {item.new && (
                    <Chip 
                      label="New" 
                      size="small" 
                      color="primary" 
                      sx={{ ml: 1 }} 
                    />
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Category Sections (Alternative View) */}
      {categories.length > 0 && (
        <Box sx={{ mt: 6 }}>
          {categories.map((category) => (
            <Box key={category} sx={{ mb: 4 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 2, 
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  '&::after': {
                    content: '""',
                    flex: 1,
                    ml: 2,
                    height: '1px',
                    backgroundColor: theme.palette.divider
                  }
                }}
              >
                {category}
              </Typography>
              <Grid container spacing={2}>
                {navItems
                  .filter(item => item.category === category)
                  .map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={`${category}-${item.title}`}>
                      <Card 
                        variant="outlined"
                        sx={{
                          '&:hover': {
                            borderColor: item.color,
                            backgroundColor: `${item.color}08`
                          }
                        }}
                      >
                        <CardActionArea 
                          sx={{ p: 2 }} 
                          onClick={() => handleNavigation(item.path)}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ 
                              bgcolor: `${item.color}20`, 
                              color: item.color,
                              mr: 2,
                              width: 40,
                              height: 40
                            }}>
                              {item.icon}
                            </Avatar>
                            <Typography variant="subtitle1">
                              {item.title}
                            </Typography>
                          </Box>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
              </Grid>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default QuickLinks;